import type { FC } from 'react';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { css, cssMap } from '@compiled/react';

import { IconButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import { Text } from '@atlaskit/primitives/compiled';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import { token } from '@atlaskit/tokens';

import { i18n } from './messages.ts';
import type { AgentCardConfig } from './agentCards.config.ts';

export type CardLayout = 'vertical' | 'horizontal';

/**
 * Identifies clicks originating from the "Rovo Agents for you" card nudge as the
 * source of the chat-new event, so the Rovo sidebar / analytics can attribute
 * the conversation to this surface.
 */
const TRY_NOW_SOURCE = 'for-you-agent-card-nudge';

const cardStyles = css({
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 0',
	minWidth: 0,
	boxSizing: 'border-box',
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.0'),
	paddingLeft: token('space.200'),
	borderRadius: token('radius.medium'),
	backgroundColor: token('elevation.surface.sunken'),
	// The preview thumbnail bleeds off the bottom edge of the card.
	overflow: 'hidden',
	// Affordances (dismiss + CTA) are hidden by default and revealed on hover or
	// keyboard focus. We drive this via custom properties on the card itself so we
	// never style child elements directly (see affordanceBaseStyles).
	'--rovo-agent-affordance-opacity': 0,
	'--rovo-agent-affordance-events': 'none',
	'&:hover, &:focus-within': {
		'--rovo-agent-affordance-opacity': 1,
		'--rovo-agent-affordance-events': 'auto',
	},
});

const layoutStyles = cssMap({
	vertical: {
		flexDirection: 'column',
	},
	horizontal: {
		flexDirection: 'row',
		alignItems: 'stretch',
		gap: token('space.300'),
	},
});

const bodyStyles = css({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
	minWidth: 0,
	gap: token('space.050'),
});

const headerRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
});

const iconImageStyles = css({
	display: 'block',
	// Badge SVGs are a hexagon that is slightly taller than wide (~18×20). Fix the
	// height and let the width follow the aspect ratio so it is never squeezed.
	height: '24px',
	width: 'auto',
	flexShrink: 0,
	objectFit: 'contain',
});

const previewWrapperStyles = css({
	display: 'flex',
	flex: '1 1 auto',
	minWidth: 0,
	marginTop: token('space.300'),
});

const previewWrapperHorizontalStyles = css({
	marginTop: token('space.0'),
	alignItems: 'center',
});

/**
 * Positioning container for the preview. Transparent — the coloured "paper" is a
 * rotated sheet behind the white sheet (see `previewTiltStyles`), so both papers
 * tilt together and the card root clips the bottom bleed.
 */
const previewStackStyles = css({
	position: 'relative',
	flex: '1 1 auto',
	width: '100%',
	// Figma preview frame is ~97px tall; the card root clips the paper stack
	// below the fold, so keep the visible window at the design height.
	height: '97px',
});

/**
 * Centring wrapper for the stacked papers. The Figma preview frame is
 * 298.17px wide inside a 344px card (~87%), positioned close to the bottom of
 * the card with a small top inset inside the visible preview window.
 */
const previewSheetWrapperStyles = css({
	position: 'absolute',
	insetBlockStart: token('space.050'),
	insetInlineStart: '50%',
	display: 'flex',
	justifyContent: 'center',
	width: '87%',
	transform: 'translateX(-50%)',
});

/**
 * Per-card tilt wrapper. BOTH papers live here so they rotate together by the
 * card's exact `--rovo-agent-tilt` (Figma `rotate-2.12deg` etc.). The
 * `::before` backing paper uses the same footprint as the white sheet and is
 * shifted slightly downward, matching the subtle saffron sheet peeking from the
 * bottom of the Figma card without over-bleeding on the sides.
 */
const previewTiltStyles = css({
	position: 'relative',
	flex: '1 1 auto',
	minWidth: 0,
	transform: 'rotate(var(--rovo-agent-tilt))',
	transformOrigin: 'center center',
	'&::before': {
		content: '""',
		position: 'absolute',
		// Backing paper is larger than the white sheet on every side so the accent
		// peeks out around it (top corners + sides are in the visible window; the
		// bottom is clipped by the card). Slightly more on the bottom/left to echo
		// the offset stacked-paper look from Figma.
		insetBlockStart: token('space.negative.100'),
		insetBlockEnd: token('space.negative.200'),
		insetInlineStart: token('space.negative.150'),
		insetInlineEnd: token('space.negative.100'),
		borderRadius: token('radius.small'),
		backgroundColor: 'var(--rovo-agent-accent)',
	},
});

/**
 * White "front paper" (Figma node 5372:175340): fills the tilt wrapper, 181.5px
 * tall (bleeds below the visible region → clipped by the card), 4.193px top
 * radius, 15.13px top padding, ADS raised shadow.
 */
const previewCardStyles = css({
	position: 'relative',
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.100'),
	width: '100%',
	height: '181.566px',
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingLeft: token('space.200'),
	borderTopLeftRadius: token('radius.small'),
	borderTopRightRadius: token('radius.small'),
	backgroundColor: token('elevation.surface'),
	// Exact ADS raised shadow from Figma.
	boxShadow: token('elevation.shadow.raised'),
	overflow: 'hidden',
});

const previewCaptionRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
});

const previewIconStyles = css({
	display: 'block',
	width: '16px',
	height: '16px',
	flexShrink: 0,
});

const previewLineStyles = css({
	height: '6px',
	borderRadius: token('radius.small'),
	backgroundColor: token('color.background.neutral'),
});

const previewLineShortStyles = css({
	width: '60%',
});

const affordanceBaseStyles = css({
	// Visibility is controlled by the card root via custom properties (revealed on
	// card hover / focus-within). Fall back to hidden if the property is unset.
	opacity: 'var(--rovo-agent-affordance-opacity, 0)',
	pointerEvents: 'var(--rovo-agent-affordance-events, none)',
	transitionDuration: '150ms',
	transitionProperty: 'opacity',
	transitionTimingFunction: 'ease-in-out',
	// Keep an affordance visible whenever it is itself focused.
	'&:focus-within': {
		opacity: 1,
		pointerEvents: 'auto',
	},
});

const dismissButtonStyles = css({
	position: 'absolute',
	top: token('space.100'),
	right: token('space.100'),
	zIndex: 1,
});

const ctaRowStyles = css({
	display: 'flex',
	alignItems: 'center',
	alignSelf: 'flex-start',
	gap: token('space.050'),
	marginTop: token('space.150'),
	padding: token('space.0'),
	border: 'none',
	backgroundColor: 'transparent',
	color: token('color.link'),
	font: token('font.body.small'),
	cursor: 'pointer',
	'&:hover': {
		textDecoration: 'underline',
	},
});

export type AgentCardProps = {
	card: AgentCardConfig;
	layout?: CardLayout;
	/**
	 * Called when the user clicks the dismiss "×". The parent removes the card
	 * from the visible set. Dismiss persistence is handled separately (PR3).
	 */
	onDismiss?: () => void;
};

/**
 * A single static "Rovo Agents for you" recommendation card.
 *
 * PR2 (CCCEMAU-3070): visuals + responsive layout. The dismiss "×" removes the
 * card from the current view.
 * PR4 (CCCEMAU-3070): the "Try it →" CTA opens the card's OOTB agent in the Rovo
 * right sidebar via a `chat-new` pubsub event, targeting the agent by its stable
 * `agentExternalConfigReference`.
 */
export const AgentCard: FC<AgentCardProps> = ({ card, layout = 'vertical', onDismiss }) => {
	const intl = useIntl();
	const isHorizontal = layout === 'horizontal';
	const publish = usePublish('ai-mate');

	const agentTitle = intl.formatMessage(card.title);

	const handleTryNow = useCallback(() => {
		publish({
			type: 'chat-new',
			source: TRY_NOW_SOURCE,
			openChatMode: 'sidebar',
			data: {
				name: agentTitle,
				dialogues: [],
				agentExternalConfigReference: card.agentExternalConfigReference,
			},
		});
	}, [publish, agentTitle, card.agentExternalConfigReference]);

	return (
		<section
			css={[cardStyles, layoutStyles[layout]]}
			data-testid={`rovo-agent-card-${card.key}`}
			// Runtime-only accent colour + preview tilt sourced from static config.
			style={
				{
					'--rovo-agent-accent': card.accentColor,
					'--rovo-agent-tilt': `${card.previewTilt}deg`,
				} as React.CSSProperties
			}
		>
			<div css={[affordanceBaseStyles, dismissButtonStyles]}>
				<IconButton
					appearance="subtle"
					spacing="compact"
					icon={CrossIcon}
					onClick={onDismiss}
					label={intl.formatMessage(i18n.dismissCardLabel, {
						agentTitle,
					})}
				/>
			</div>
			<div css={bodyStyles}>
				<div css={headerRowStyles}>
					<img css={iconImageStyles} src={card.icon} alt="" aria-hidden />
					<Text weight="bold" color="color.text">
						<FormattedMessage {...card.title} />
					</Text>
				</div>
				<Text size="small" color="color.text.subtle">
					<FormattedMessage {...card.description} />
				</Text>
				<button
					type="button"
					css={[ctaRowStyles, affordanceBaseStyles]}
					data-testid={`rovo-agent-cta-${card.key}`}
					onClick={handleTryNow}
					aria-label={intl.formatMessage(i18n.tryItCtaLabel, { agentTitle })}
				>
					<FormattedMessage {...i18n.tryItCta} />
					<ArrowRightIcon label="" size="small" color="currentColor" />
				</button>
			</div>
			<div css={[previewWrapperStyles, isHorizontal && previewWrapperHorizontalStyles]}>
				<div css={previewStackStyles} aria-hidden>
					<div css={previewSheetWrapperStyles}>
						<div css={previewTiltStyles}>
							<div css={previewCardStyles}>
								<div css={previewCaptionRowStyles}>
									<img css={previewIconStyles} src={card.previewIcon} alt="" aria-hidden />
									<Text size="small" color="color.text">
										<FormattedMessage {...card.previewCaption} />
									</Text>
								</div>
								<div css={previewLineStyles} aria-hidden />
								<div css={[previewLineStyles, previewLineShortStyles]} aria-hidden />
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};
